import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Card, Form } from 'semantic-ui-react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { AppTable, FlexRow, FlexRowBetween, LoaderBar } from 'components'
import { AppEditButton, AppRemoveButtonPopup } from 'components/Popup'
import {
    FormDateField,
    FormDropdownField,
    FormTextAreaFormField,
    FormTextFormField,
    SubmitCancelButtons,
} from 'elements'
import {
    AuthService,
    DateUtility,
    PaginationType,
    RotationService,
    extractRotationScheduleId,
    resolveRotationScheduleIdAfterCreate,
    parseRotationTemplateIds,
    toRotationTemplateIdString,
    UsersService,
    TemplateService,
    monthYearFromDate,
    rotationApiDateTime,
} from 'utility'
import { ScheduleByWeekSection } from './ScheduleByWeekSection'
import { WeeklyRotationEntryForm } from './WeeklyRotationEntryForm'
import { RotationPageWrap } from './rotation-section-styles'

const WK_COMPANY_ID = 34930

const formatEmployeeNamesFromDetails = detailsList => {
    const names = [
        ...new Set(
            (detailsList || [])
                .map(detail => detail?.EmployeeName)
                .filter(name => name != null && String(name).trim() !== ''),
        ),
    ]
    return names.length ? names.join(', ') : '—'
}

const enrichSchedulesWithEmployees = async (userId, scheduleList) => {
    const list = scheduleList || []
    if (!userId || list.length === 0) return list

    return Promise.all(
        list.map(async schedule => {
            const id = extractRotationScheduleId(schedule)
            if (!id) {
                return { ...schedule, EmployeeNames: '—' }
            }
            try {
                const detailsRes = await RotationService.getDetails(userId, id)
                return {
                    ...schedule,
                    EmployeeNames: formatEmployeeNamesFromDetails(detailsRes),
                }
            } catch {
                return { ...schedule, EmployeeNames: '—' }
            }
        }),
    )
}

const RotationScheduleCard = ({ item, onEdit, onDelete }) => (
    <Card fluid>
        <Card.Content>
            <Card.Header>{item.Title || 'Rotation'}</Card.Header>
            <Card.Meta>Employee: {item.EmployeeNames || '—'}</Card.Meta>
            <Card.Meta>Notes: {item.Notes || '—'}</Card.Meta>
            <Card.Meta>
                Created On:{' '}
                {item.CreatedDate
                    ? new Date(item.CreatedDate).toLocaleDateString()
                    : '—'}
            </Card.Meta>
            <Card.Description>
                <FlexRow className="justify-content-end">
                    <AppEditButton onClick={() => onEdit(item)} />
                    <AppRemoveButtonPopup onConfirm={() => onDelete(item)} />
                </FlexRow>
            </Card.Description>
        </Card.Content>
    </Card>
)

const MonthlyRotationDefaultSchema = yup.object().shape({
    Title: yup.string().required('Title is required'),
    ShiftId: yup.mixed().required('Shift is required'),
    EmployeeId: yup.mixed().required('Auditor is required'),
    WorkPlaceId: yup.mixed().required('Work Place is required'),
    FromDate: yup.string().required('From date is required'),
    ToDate: yup.string().required('To date is required'),
    SendEmail: yup.mixed().required('Send Email is required'),
    TemplateId: yup.mixed().nullable(),
    Comments: yup.string().nullable(),
})

const MonthlyRotationWkSchema = yup.object().shape({
    Title: yup.string().required('Title is required'),
    ShiftId: yup.mixed().required('Shift is required'),
    EmployeeId: yup.mixed().required('Auditor is required'),
    WorkPlaceId: yup.mixed().nullable(),
    ScheduleMonth: yup.mixed().required('Month & Year is required'),
    ScheduleYear: yup.mixed().required('Month & Year is required'),
    EmailNotifyMode: yup.string().nullable(),
    SharedTemplateIds: yup.mixed().nullable(),
    Comments: yup.string().nullable(),
})

export const MonthlyRotation = () => {
    const user = AuthService.getUser()
    const userId = user?.unique_id || user?.UserId || user?.Id
    const isWkCompany =
        Number(user?.Companies?.[0]?.CompanyId || user?.CompanyId) === WK_COMPANY_ID

    const now = new Date()
    const monthName = now.toLocaleString('default', { month: 'long' })
    const defaultTitle = `Monthly Rotation - ${monthName} ${now.getFullYear()}`

    const [loading, setLoading] = useState(false)
    const [lookupLoading, setLookupLoading] = useState(false)
    const [scheduleId, setScheduleId] = useState(null)
    const [editingScheduleId, setEditingScheduleId] = useState(null)
    const [editingDetailId, setEditingDetailId] = useState(null)
    const [schedules, setSchedules] = useState([])
    const [details, setDetails] = useState([])
    const [shifts, setShifts] = useState([])
    const [employees, setEmployees] = useState([])
    const [workPlaces, setWorkPlaces] = useState([])
    const [templates, setTemplates] = useState([])
    const [detailFilter, setDetailFilter] = useState({
        PageNumber: 1,
        PageSize: 10,
    })
    const [scheduleFilter, setScheduleFilter] = useState({
        PageNumber: 1,
        PageSize: 10,
    })

    const weekFormRef = useRef(null)

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        watch,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(
            isWkCompany ? MonthlyRotationWkSchema : MonthlyRotationDefaultSchema,
        ),
        defaultValues: isWkCompany
            ? {
                  Title: defaultTitle,
                  ScheduleMonth: now.getMonth() + 1,
                  ScheduleYear: now.getFullYear(),
                  EmailNotifyMode: 'per_week',
                  SharedTemplateIds: [],
              }
            : {
                  Title: defaultTitle,
              },
    })

    const reloadSchedulesAndDetails = useCallback(
        async (preferredScheduleId = null) => {
            if (!userId) return
            try {
                const scheduleRes = await RotationService.getSchedules(userId)
                let scheduleList = scheduleRes || []

                // Always use RotationScheduleId from API for subsequent calls
                let currentScheduleId =
                    preferredScheduleId ||
                    extractRotationScheduleId(scheduleList[0]) ||
                    null

                if (!currentScheduleId && scheduleList.length === 0) {
                    const createRes = await RotationService.createSchedule(
                        userId,
                        {
                            Title: defaultTitle,
                            Notes: '',
                        },
                    )
                    currentScheduleId =
                        extractRotationScheduleId(createRes) || null
                    const refreshed = await RotationService.getSchedules(userId)
                    scheduleList = refreshed || []
                }

                const enrichedSchedules = await enrichSchedulesWithEmployees(
                    userId,
                    scheduleList,
                )
                setSchedules(enrichedSchedules)

                if (currentScheduleId) {
                    setScheduleId(currentScheduleId)
                    const detailsRes = await RotationService.getDetails(
                        userId,
                        currentScheduleId,
                    )
                    setDetails(detailsRes || [])
                } else {
                    setDetails([])
                }
            } catch (err) {
                console.error(
                    'Error loading Monthly Rotation schedules/details:',
                    err,
                )
            }
        },
        [userId, defaultTitle],
    )

    // Load lookup data and schedules
    useEffect(() => {
        if (!userId) return

        const loadLookups = async () => {
            setLookupLoading(true)
            try {
                const [shiftRes, empRes, wpRes, templateRes] = await Promise.all([
                    RotationService.getShifts(userId),
                    UsersService.getTeamUsers({
                        PageNumber: 1,
                        PageSize: 5000000,
                        SearchText: '',
                        all: 'All',
                    }),
                    RotationService.getWorkPlaces(userId),
                    TemplateService.templateList({
                        PageNumber: 1,
                        PageSize: 5000000,
                        SearchText: '',
                        all: PaginationType.All,
                    }),
                ])

                setShifts(
                    (shiftRes || []).map(s => ({
                        text: s.ShiftName || s.Name,
                        value: s.Id ?? s.ShiftId,
                    })),
                )
                setEmployees(
                    (empRes || []).map(e => ({
                        text: e.Name || e.EmployeeName || e.UserName || 'Team Member',
                        value: e.unique_id ?? e.Id ?? e.EmployeeId,
                    })),
                )
                setWorkPlaces(
                    (wpRes || []).map(w => ({
                        text: w.WorkPlaceName || w.Name,
                        value: w.Id ?? w.WorkPlaceId,
                    })),
                )
                setTemplates(
                    (templateRes?.Items || []).map(t => ({
                        text: t.TemplateName || 'Template',
                        value: t.Id,
                    })),
                )
            } catch (err) {
                console.error('Error loading Monthly Rotation lookups:', err)
            } finally {
                setLookupLoading(false)
            }
        }

        // Run lookups and schedules in parallel so dropdowns fill quickly
        loadLookups()
        reloadSchedulesAndDetails()
    }, [userId, reloadSchedulesAndDetails])

    const sendEmailOptions = useMemo(
        () => [
            { text: 'Yes', value: 1 },
            { text: 'No', value: 0 },
        ],
        [],
    )

    const handleDeleteDetail = useCallback(
        async detail => {
            if (!userId) return
            const detailId =
                detail?.Id ||
                detail?.RotationScheduleDetailId ||
                detail?.DetailId
            if (!detailId) return

            setLoading(true)
            try {
                await RotationService.deleteDetail(userId, detailId)
                const detailsRes = await RotationService.getDetails(
                    userId,
                    scheduleId,
                )
                setDetails(detailsRes || [])
                setSchedules(prev =>
                    (prev || []).map(schedule =>
                        extractRotationScheduleId(schedule) === scheduleId
                            ? {
                                  ...schedule,
                                  EmployeeNames:
                                      formatEmployeeNamesFromDetails(detailsRes),
                              }
                            : schedule,
                    ),
                )
            } catch (err) {
                console.error(
                    'Error deleting Monthly Rotation detail:',
                    err,
                )
            } finally {
                setLoading(false)
            }
        },
        [userId, scheduleId],
    )

    const handleEditSchedule = useCallback(
        async schedule => {
            const id = schedule?.RotationScheduleId
            if (!id || !userId) return

            setScheduleId(id)
            setEditingScheduleId(id)
            setEditingDetailId(null)
            setValue('Title', schedule.Title || defaultTitle)
            setValue('Comments', schedule.Notes || '')

            try {
                const detailsRes = await RotationService.getDetails(userId, id)
                const detailList = detailsRes || []

                if (isWkCompany && detailList.length > 0) {
                    const firstDetail = detailList[0]
                    const detailId =
                        firstDetail.Id ||
                        firstDetail.RotationScheduleDetailId ||
                        firstDetail.DetailId ||
                        firstDetail.RotationDetailId
                    setEditingDetailId(detailId || null)

                    if (firstDetail.FromDate) {
                        const { month, year } = monthYearFromDate(
                            new Date(firstDetail.FromDate),
                        )
                        weekFormRef.current?.loadFromDetails(
                            detailList,
                            month,
                            year,
                        )
                    }

                    if (typeof firstDetail.SendEmail === 'number') {
                        setValue('SendEmail', firstDetail.SendEmail)
                    }
                    return
                }

                const firstDetail = detailList[0]
                if (firstDetail) {
                    const detailId =
                        firstDetail.Id ||
                        firstDetail.RotationScheduleDetailId ||
                        firstDetail.DetailId
                    setEditingDetailId(detailId || null)

                    if (firstDetail.ShiftId != null) {
                        setValue('ShiftId', firstDetail.ShiftId)
                    }
                    if (firstDetail.EmployeeId != null) {
                        setValue('EmployeeId', firstDetail.EmployeeId)
                    }
                    if (firstDetail.WorkPlaceId != null) {
                        setValue('WorkPlaceId', firstDetail.WorkPlaceId)
                    }
                    if (firstDetail.TemplateId != null) {
                        if (isWkCompany) {
                            setValue(
                                'WeekTemplateIds',
                                parseRotationTemplateIds(firstDetail.TemplateId),
                            )
                        } else {
                            setValue('TemplateId', firstDetail.TemplateId)
                        }
                    }

                    if (firstDetail.FromDate) {
                        if (isWkCompany) {
                            const { month, year } = monthYearFromDate(
                                new Date(firstDetail.FromDate),
                            )
                            if (month) setValue('ScheduleMonth', month)
                            if (year) setValue('ScheduleYear', year)
                        } else {
                            setValue('FromDate', new Date(firstDetail.FromDate))
                        }
                    }
                    if (firstDetail.ToDate && !isWkCompany) {
                        setValue('ToDate', new Date(firstDetail.ToDate))
                    }

                    if (typeof firstDetail.SendEmail === 'number') {
                        setValue('SendEmail', firstDetail.SendEmail)
                    } else if (typeof firstDetail.SendEmail === 'boolean') {
                        setValue('SendEmail', firstDetail.SendEmail ? 1 : 0)
                    }
                }
            } catch (err) {
                console.error(
                    'Error loading Monthly Rotation details for edit:',
                    err,
                )
            }
        },
        [userId, setValue, defaultTitle, isWkCompany],
    )

    const handleDeleteSchedule = useCallback(
        async schedule => {
            if (!userId) return
            const id = schedule?.RotationScheduleId
            if (!id) return

            setLoading(true)
            try {
                await RotationService.deleteSchedule(userId, id)
                const scheduleRes = await RotationService.getSchedules(userId)
                const nextSchedules = await enrichSchedulesWithEmployees(
                    userId,
                    scheduleRes || [],
                )
                setSchedules(nextSchedules)

                let nextScheduleId = scheduleId
                if (id === scheduleId) {
                    nextScheduleId =
                        nextSchedules[0]?.RotationScheduleId ||
                        null
                    setScheduleId(nextScheduleId)
                    setEditingScheduleId(null)
                    setEditingDetailId(null)
                }

                if (nextScheduleId) {
                    const detailsRes = await RotationService.getDetails(
                        userId,
                        nextScheduleId,
                    )
                    setDetails(detailsRes || [])
                } else {
                    setDetails([])
                }
            } catch (err) {
                console.error('Error deleting Monthly Rotation schedule:', err)
            } finally {
                setLoading(false)
            }
        },
        [userId, scheduleId],
    )

    const columns = useMemo(
        () => [
            {
                title: 'Shift',
                key: 'ShiftName',
            },
            {
                title: 'Auditor',
                key: 'EmployeeName',
            },
            {
                title: 'Work Place',
                key: 'WorkPlaceName',
            },
            {
                title: 'From',
                key: 'FromDate',
            },
            {
                title: 'To',
                key: 'ToDate',
            },
            {
                title: 'Send Email',
                render: item => (item.SendEmail ? 'Yes' : 'No'),
            },
            {
                title: 'Comments',
                key: 'Comments',
            },
            {
                title: 'Actions',
                render: item => (
                    <AppRemoveButtonPopup
                        onConfirm={() => handleDeleteDetail(item)}
                    />
                ),
            },
        ],
        [handleDeleteDetail],
    )

    const scheduleColumns = useMemo(
        () => [
            {
                title: 'Title',
                key: 'Title',
            },
            {
                title: 'Employee',
                key: 'EmployeeNames',
            },
            {
                title: 'Notes',
                key: 'Notes',
            },
            {
                title: 'Created On',
                render: item =>
                    item.CreatedDate
                        ? new Date(item.CreatedDate).toLocaleDateString()
                        : '',
            },
            {
                title: 'Actions',
                render: item => (
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                        <AppEditButton onClick={() => handleEditSchedule(item)} />
                        <AppRemoveButtonPopup
                            onConfirm={() => handleDeleteSchedule(item)}
                        />
                    </div>
                ),
            },
        ],
        [handleEditSchedule, handleDeleteSchedule],
    )

    const pagedDetails = useMemo(() => {
        const list = details || []
        const { PageNumber, PageSize } = detailFilter
        const start = (PageNumber - 1) * PageSize
        return list.slice(start, start + PageSize)
    }, [details, detailFilter])

    const pagedSchedules = useMemo(() => {
        const list = schedules || []
        const { PageNumber, PageSize } = scheduleFilter
        const start = (PageNumber - 1) * PageSize
        return list.slice(start, start + PageSize)
    }, [schedules, scheduleFilter])

    const handleDetailPageChange = page => {
        setDetailFilter(prev => ({
            ...prev,
            PageNumber: page || 1,
        }))
    }

    const handleSchedulePageChange = page => {
        setScheduleFilter(prev => ({
            ...prev,
            PageNumber: page || 1,
        }))
    }

    const submit = async (formData, isDraft = false) => {
        if (!userId) return
        setLoading(true)
        try {
            const scheduleTitle = formData.Title || defaultTitle
            const scheduleNotes = formData.Comments || ''

            let targetScheduleId = scheduleId

            if (editingScheduleId) {
                await RotationService.updateSchedule(
                    userId,
                    editingScheduleId,
                    {
                        Title: scheduleTitle,
                        IsActive: !isDraft,
                        Notes: scheduleNotes,
                    },
                )
                targetScheduleId = editingScheduleId
            } else {
                const createdSchedule = await RotationService.createSchedule(
                    userId,
                    {
                        Title: scheduleTitle,
                        Notes: scheduleNotes,
                        IsActive: !isDraft,
                    },
                )
                targetScheduleId =
                    await resolveRotationScheduleIdAfterCreate(
                        userId,
                        createdSchedule,
                        scheduleTitle,
                    )
            }

            if (!targetScheduleId) {
                throw new Error(
                    'Could not resolve RotationScheduleId after creating schedule.',
                )
            }

            setScheduleId(targetScheduleId)

            if (isWkCompany) {
                const weekPayload = weekFormRef.current?.getSavePayload(isDraft)
                const weeks = weekPayload?.weeks || []
                if (weeks.length === 0) {
                    throw new Error('No weeks selected to save.')
                }

                if (editingScheduleId) {
                    const existing = await RotationService.getDetails(
                        userId,
                        targetScheduleId,
                    )
                    await Promise.all(
                        (existing || []).map(detail => {
                            const detailId =
                                detail?.Id ||
                                detail?.RotationScheduleDetailId ||
                                detail?.DetailId ||
                                detail?.RotationDetailId
                            if (!detailId) return Promise.resolve()
                            return RotationService.deleteDetail(
                                userId,
                                detailId,
                            )
                        }),
                    )
                }

                await weeks.reduce(async (prev, week) => {
                    await prev
                    await RotationService.createDetail(userId, {
                        RotationScheduleId: targetScheduleId,
                        EmployeeId: formData.EmployeeId,
                        ShiftId: formData.ShiftId,
                        WorkPlaceId: week.workPlaceId,
                        TemplateId: toRotationTemplateIdString(
                            week.templateIds,
                        ),
                        FromDate: rotationApiDateTime(week.startDate),
                        ToDate: rotationApiDateTime(week.endDate),
                        SendEmail: week.sendEmail,
                        Comments: formData.Comments || '',
                    })
                }, Promise.resolve())
            } else {
                const formattedFromDate = formData.FromDate
                    ? DateUtility.dateToLocalISO(
                          DateUtility.startDay(new Date(formData.FromDate)),
                      )
                    : null
                const formattedToDate = formData.ToDate
                    ? DateUtility.dateToLocalISO(
                          DateUtility.endDay(new Date(formData.ToDate)),
                      )
                    : null
                const templateId =
                    formData.TemplateId != null && formData.TemplateId !== ''
                        ? String(formData.TemplateId)
                        : null

                const baseDetailPayload = {
                    EmployeeId: formData.EmployeeId,
                    ShiftId: formData.ShiftId,
                    WorkPlaceId: formData.WorkPlaceId,
                    TemplateId: templateId,
                    FromDate: formattedFromDate,
                    ToDate: formattedToDate,
                    SendEmail: formData.SendEmail,
                    Comments: formData.Comments || '',
                }

                if (editingDetailId) {
                    await RotationService.updateDetail(
                        userId,
                        editingDetailId,
                        baseDetailPayload,
                    )
                } else {
                    await RotationService.createDetail(userId, {
                        RotationScheduleId: targetScheduleId,
                        ...baseDetailPayload,
                    })
                }
            }
            // Refresh schedules + details (runs GET /Schedules)
            await reloadSchedulesAndDetails(targetScheduleId)

            reset(
                isWkCompany
                    ? {
                          Title: defaultTitle,
                          ShiftId: null,
                          EmployeeId: null,
                          WorkPlaceId: null,
                          SharedTemplateIds: [],
                          ScheduleMonth: now.getMonth() + 1,
                          ScheduleYear: now.getFullYear(),
                          EmailNotifyMode: 'per_week',
                          Comments: '',
                      }
                    : {
                          Title: defaultTitle,
                          ShiftId: null,
                          EmployeeId: null,
                          WorkPlaceId: null,
                          TemplateId: null,
                          FromDate: null,
                          ToDate: null,
                          SendEmail: 1,
                          Comments: '',
                      },
            )
            setEditingScheduleId(null)
            setEditingDetailId(null)
            if (isWkCompany) {
                weekFormRef.current?.resetGrid(
                    now.getMonth() + 1,
                    now.getFullYear(),
                )
            }
        } catch (err) {
            console.error('Error saving Monthly Rotation detail:', err)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        // Default SendEmail to yes
        setValue('SendEmail', 1)
    }, [setValue])

    const watchedEmployeeId = watch('EmployeeId')
    const watchedTemplateId = watch('TemplateId')

    const handleWkCancel = () => {
        reset(wkResetValues)
        setEditingScheduleId(null)
        setEditingDetailId(null)
        weekFormRef.current?.resetGrid(
            now.getMonth() + 1,
            now.getFullYear(),
        )
    }

    const handleWkSave = handleSubmit(formData => submit(formData, false))
    const handleWkSaveDraft = handleSubmit(formData => submit(formData, true))

    const wkResetValues = {
        Title: defaultTitle,
        ShiftId: null,
        EmployeeId: null,
        WorkPlaceId: null,
        SharedTemplateIds: [],
        ScheduleMonth: now.getMonth() + 1,
        ScheduleYear: now.getFullYear(),
        EmailNotifyMode: 'per_week',
        Comments: '',
    }

    const defaultResetValues = {
        Title: defaultTitle,
        ShiftId: null,
        EmployeeId: null,
        WorkPlaceId: null,
        TemplateId: null,
        FromDate: null,
        ToDate: null,
        SendEmail: 1,
        Comments: '',
    }

    const rotationSchedulesTable = (
        <div className="mt-4">
            <h5>Rotation Schedules</h5>
            <AppTable
                data={pagedSchedules}
                columns={scheduleColumns}
                emptyMessage="No schedules configured"
                filter={scheduleFilter}
                totalCount={(schedules || []).length}
                pageChanged={handleSchedulePageChange}
                mobileCard={
                    <RotationScheduleCard
                        onEdit={handleEditSchedule}
                        onDelete={handleDeleteSchedule}
                    />
                }
            />
        </div>
    )

    if (!isWkCompany) {
        return (
            <div>
                {(loading || lookupLoading) && (
                    <LoaderBar content="Loading..." />
                )}
                <FlexRowBetween style={{ marginBottom: '16px' }}>
                    <h4>Monthly Rotation</h4>
                </FlexRowBetween>

                <Form noValidate>
                    <div className="my-2">
                        <Form.Group widths="equal">
                            <FormTextFormField
                                control={control}
                                label="Title"
                                name="Title"
                                placeholder="Enter title"
                                errors={errors?.Title}
                            />
                        </Form.Group>
                        <Form.Group widths="equal">
                            <FormDropdownField
                                control={control}
                                label="Shift"
                                name="ShiftId"
                                placeholder="Select shift"
                                errors={errors?.ShiftId}
                                options={shifts}
                                loading={lookupLoading}
                                search
                            />
                            <FormDropdownField
                                control={control}
                                label="Auditor"
                                name="EmployeeId"
                                placeholder="Select auditor"
                                errors={errors?.EmployeeId}
                                options={employees}
                                loading={lookupLoading}
                                search
                            />
                            <FormDropdownField
                                control={control}
                                label="Work Place"
                                name="WorkPlaceId"
                                placeholder="Select work place"
                                errors={errors?.WorkPlaceId}
                                options={workPlaces}
                                loading={lookupLoading}
                                search
                            />
                            <FormDropdownField
                                control={control}
                                label="Template"
                                name="TemplateId"
                                placeholder="Select template"
                                errors={errors?.TemplateId}
                                options={templates}
                                loading={lookupLoading}
                                search
                            />
                        </Form.Group>
                        <Form.Group widths="equal">
                            <FormDateField
                                control={control}
                                label="From"
                                name="FromDate"
                                placeholder="From date"
                                errors={errors?.FromDate}
                            />
                            <FormDateField
                                control={control}
                                label="To"
                                name="ToDate"
                                placeholder="To date"
                                errors={errors?.ToDate}
                            />
                            <FormDropdownField
                                control={control}
                                label="Send Email"
                                name="SendEmail"
                                placeholder="Send Email"
                                errors={errors?.SendEmail}
                                options={sendEmailOptions}
                                defaultValue={1}
                            />
                        </Form.Group>
                        <ScheduleByWeekSection
                            employeeId={watchedEmployeeId}
                            templateId={watchedTemplateId}
                        />
                        <Form.Group widths="equal">
                            <FormTextAreaFormField
                                control={control}
                                label="Comments"
                                name="Comments"
                                placeholder="Comments"
                                errors={errors?.Comments}
                            />
                        </Form.Group>
                    </div>
                    <SubmitCancelButtons
                        loading={loading}
                        onSubmit={handleSubmit(submit)}
                        onCancel={() => {
                            reset(defaultResetValues)
                            setEditingScheduleId(null)
                            setEditingDetailId(null)
                        }}
                    />
                </Form>

                {false && (
                    <div className="mt-4">
                        <h5>Rotation Details</h5>
                        <AppTable
                            data={pagedDetails}
                            columns={columns}
                            emptyMessage="No rotation details configured"
                            filter={detailFilter}
                            totalCount={(details || []).length}
                            pageChanged={handleDetailPageChange}
                            mobileCard={null}
                        />
                    </div>
                )}
                {rotationSchedulesTable}
            </div>
        )
    }

    return (
        <RotationPageWrap>
            {(loading || lookupLoading) && (
                <LoaderBar content="Loading..." />
            )}

            <Form noValidate>
                <WeeklyRotationEntryForm
                    ref={weekFormRef}
                    control={control}
                    errors={errors}
                    watch={watch}
                    setValue={setValue}
                    shifts={shifts}
                    employees={employees}
                    workPlaces={workPlaces}
                    templates={templates}
                    lookupLoading={lookupLoading}
                    loading={loading}
                    onSave={handleWkSave}
                    onSaveDraft={handleWkSaveDraft}
                    onCancel={handleWkCancel}
                />
            </Form>

            {rotationSchedulesTable}
        </RotationPageWrap>
    )
}